
import React, { useMemo } from 'react';
import { 
  Download, PieChart as PieChartIcon, TrendingUp, AlertTriangle, 
  CalendarClock, Ruler, Wallet, Activity, ArrowRight, Lock,
  ChevronRight, Circle
} from 'lucide-react';
import { 
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, AreaChart, 
  Area, XAxis, YAxis, CartesianGrid, Legend, BarChart, Bar
} from 'recharts';
import { Project, ExpenseCategory, PlanTier } from '../types';
import { generateProjectReport } from '../services/reportService';
import { formatCurrency } from '../utils/formatters';

interface StatsViewProps {
  project: Project;
  currentUser: string;
  onEditStages: () => void;
  planTier: PlanTier;
  onUpgrade: () => void;
}

const CHART_COLORS = {
  emerald: '#10B981',
  blue: '#3B82F6',
  purple: '#8B5CF6',
  amber: '#F59E0B',
  rose: '#F43F5E',
  slate: '#94a3b8'
};

const CATEGORY_COLORS: Record<string, string> = {
  [ExpenseCategory.MATERIALS]: CHART_COLORS.emerald,
  [ExpenseCategory.LABOR]: CHART_COLORS.blue,
  [ExpenseCategory.PROJECTS]: CHART_COLORS.purple,
  [ExpenseCategory.DOCUMENTATION]: CHART_COLORS.amber,
  [ExpenseCategory.OTHERS]: CHART_COLORS.slate,
};

// Componente de Tooltip Customizado para Elegância Máxima
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md p-4 rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-800 animate-fadeIn">
        <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">{label}</p>
        <p className="text-sm font-black text-slate-900 dark:text-white">
          {formatCurrency(payload[0].value)}
        </p>
      </div>
    );
  }
  return null;
};

export const StatsView: React.FC<StatsViewProps> = ({ project, currentUser, onEditStages, planTier, onUpgrade }) => {
  
  const allowPdf = planTier !== 'trial';

  const handlePdfExport = () => {
    if (!allowPdf) {
        onUpgrade();
        return;
    }
    generateProjectReport(project, currentUser);
  };

  const analytics = useMemo(() => {
    const totalSpent = project.expenses.reduce((acc, curr) => acc + curr.amount, 0);
    const paidAmount = project.expenses.filter(e => e.isPaid).reduce((acc, curr) => acc + curr.amount, 0);
    const pendingAmount = totalSpent - paidAmount;
    const totalBudget = project.initialBudget || 1; 
    const area = project.areaSize || 0;
    const costPerM2 = area > 0 ? totalSpent / area : 0;
    
    // Gráfico de Pizza (Categorias)
    const categoryData = Object.values(ExpenseCategory).map(cat => {
      const val = project.expenses.filter(e => e.category === cat).reduce((a, b) => a + b.amount, 0);
      return { name: cat, value: val, color: CATEGORY_COLORS[cat] || CHART_COLORS.slate };
    }).filter(d => d.value > 0);

    // Linha do Tempo (Evolução)
    const expensesByMonth: Record<string, number> = {};
    project.expenses.forEach(e => {
      const date = new Date(e.date);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      expensesByMonth[key] = (expensesByMonth[key] || 0) + e.amount;
    });

    const sortedMonths = Object.keys(expensesByMonth).sort();
    let accumulated = 0;
    const timelineData = sortedMonths.map(month => {
      accumulated += expensesByMonth[month];
      const [y, m] = month.split('-');
      const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      return {
        date: `${monthNames[parseInt(m) - 1]} ${y.slice(2)}`,
        amount: accumulated,
        raw: month
      };
    });

    // Análise de Etapas
    const stagesData = project.stages.map(stage => {
      const spent = project.expenses.filter(e => e.stageId === stage.id).reduce((a, b) => a + b.amount, 0);
      const budget = stage.expectedBudget || 0;
      const consumptionPercent = budget > 0 ? (spent / budget) * 100 : (spent > 0 ? 100 : 0);
      
      let status: 'healthy' | 'warning' | 'danger' = 'healthy';
      if (consumptionPercent > 100) status = 'danger';
      else if (consumptionPercent > 85) status = 'warning';
      
      return {
        id: stage.id,
        name: stage.name,
        budget,
        spent,
        consumptionPercent,
        status
      };
    });

    return {
      totalSpent,
      paidAmount,
      pendingAmount,
      costPerM2,
      categoryData,
      timelineData,
      stagesData
    };
  }, [project]);

  const isLocked = !project.budgetMode || project.budgetMode === 'general';

  return (
    <div className="space-y-8 animate-fadeIn pb-24 max-w-5xl mx-auto">
      
      {/* HEADER DINÂMICO */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
        <div>
           <h2 className="text-4xl font-black uppercase tracking-tighter text-slate-900 dark:text-white leading-none mb-2">Relatórios</h2>
           <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-2">
             <div className="size-2 rounded-full bg-emerald-500 animate-pulse"></div>
             Performance financeira em tempo real
           </p>
        </div>
        <button 
          onClick={handlePdfExport}
          className={`group flex items-center gap-3 px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-xl ${allowPdf ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:scale-105 active:scale-95' : 'bg-slate-200 dark:bg-slate-800 text-slate-500 cursor-pointer'}`}
        >
          {allowPdf ? <Download size={18} className="group-hover:translate-y-0.5 transition-transform" /> : <Lock size={18} />} 
          {allowPdf ? 'Baixar PDF Completo' : 'Desbloquear Relatório PDF'}
        </button>
      </div>

      {/* KPI GRID - CARDS MODERNOS */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
         <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 p-6 opacity-5"><Ruler size={64}/></div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Custo por m²</p>
            <h3 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">{formatCurrency(analytics.costPerM2)}</h3>
            <div className="mt-4 flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest bg-slate-50 dark:bg-slate-800 w-fit px-3 py-1 rounded-full">
               {project.areaSize} Metros Quadrados
            </div>
         </div>

         <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 p-6 opacity-5"><CalendarClock size={64}/></div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Comprometido (A Pagar)</p>
            <h3 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">{formatCurrency(analytics.pendingAmount)}</h3>
            <div className="mt-4 flex items-center gap-2 text-[10px] font-bold text-amber-600 uppercase tracking-widest bg-amber-50 dark:bg-amber-900/20 w-fit px-3 py-1 rounded-full">
               Fluxo de Caixa Pendente
            </div>
         </div>

         <div className={`p-8 rounded-[2.5rem] border shadow-sm relative overflow-hidden transition-all ${analytics.totalSpent > project.initialBudget ? 'bg-rose-50 border-rose-100 dark:bg-rose-900/10 dark:border-rose-900/30' : 'bg-emerald-50 border-emerald-100 dark:bg-emerald-900/10 dark:border-emerald-900/30'}`}>
            <div className="absolute top-0 right-0 p-6 opacity-10"><Activity size={64}/></div>
            <p className="text-[10px] font-black uppercase tracking-widest opacity-60 mb-2">Status do Orçamento</p>
            <h3 className={`text-3xl font-black tracking-tight ${analytics.totalSpent > project.initialBudget ? 'text-rose-700 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
               {analytics.totalSpent > project.initialBudget ? 'Excedido' : 'Sob Controle'}
            </h3>
            <p className="text-[10px] font-bold mt-4 uppercase opacity-70 tracking-widest">
               Variação de {((analytics.totalSpent / (project.initialBudget || 1) - 1) * 100).toFixed(1)}%
            </p>
         </div>
      </div>

      {/* GRÁFICO DE LINHA - EVOLUÇÃO ACUMULADA */}
      <div className="bg-white dark:bg-slate-900 p-8 sm:p-10 rounded-[3rem] shadow-sm border border-slate-100 dark:border-slate-800">
        <div className="flex justify-between items-center mb-10">
           <div>
              <h3 className="text-xl font-black uppercase tracking-tight flex items-center gap-3">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl text-emerald-600"><TrendingUp size={20} /></div>
                Fluxo de Evolução
              </h3>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2 ml-12">Gastos acumulados mês a mês</p>
           </div>
        </div>
        
        <div className="h-[350px] w-full">
           {analytics.timelineData.length > 0 ? (
             <ResponsiveContainer width="100%" height="100%">
               <AreaChart data={analytics.timelineData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                 <defs>
                   <linearGradient id="colorEvo" x1="0" y1="0" x2="0" y2="1">
                     <stop offset="5%" stopColor={CHART_COLORS.emerald} stopOpacity={0.15}/>
                     <stop offset="95%" stopColor={CHART_COLORS.emerald} stopOpacity={0}/>
                   </linearGradient>
                 </defs>
                 <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                 <XAxis 
                    dataKey="date" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{fill: '#94a3b8', fontSize: 10, fontWeight: 800}} 
                    dy={15}
                 />
                 <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{fill: '#94a3b8', fontSize: 10, fontWeight: 700}} 
                    tickFormatter={(val) => `R$${Math.round(val/1000)}k`}
                 />
                 <Tooltip content={<CustomTooltip />} cursor={{ stroke: CHART_COLORS.emerald, strokeWidth: 1, strokeDasharray: '4 4' }} />
                 <Area 
                    type="monotone" 
                    dataKey="amount" 
                    stroke={CHART_COLORS.emerald} 
                    strokeWidth={4}
                    fillOpacity={1} 
                    fill="url(#colorEvo)"
                    animationDuration={2000}
                 />
               </AreaChart>
             </ResponsiveContainer>
           ) : (
             <div className="h-full flex flex-col items-center justify-center border-4 border-dashed border-slate-50 dark:border-slate-800 rounded-[2rem]">
                <Activity size={40} className="text-slate-200 dark:text-slate-700 mb-4" />
                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest text-center">Aguardando mais lançamentos<br/>para gerar estatísticas</p>
             </div>
           )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* GRÁFICO DONUT - CATEGORIAS */}
        <div className="bg-white dark:bg-slate-900 p-8 sm:p-10 rounded-[3rem] shadow-sm border border-slate-100 dark:border-slate-800 flex flex-col">
           <div className="mb-8">
             <h3 className="text-xl font-black uppercase tracking-tight flex items-center gap-3">
               <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl text-blue-600"><PieChartIcon size={20} /></div>
               Mix de Investimento
             </h3>
             <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2 ml-12">Distribuição por categoria</p>
           </div>
           
           <div className="flex-1 min-h-[320px] relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analytics.categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={85}
                    outerRadius={115}
                    paddingAngle={8}
                    dataKey="value"
                    stroke="none"
                    animationBegin={0}
                    animationDuration={1500}
                  >
                    {analytics.categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} cornerRadius={10} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              {/* Centro do Donut com Informação Vital */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                 <div className="text-center animate-fadeIn">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Total Lançado</p>
                    <p className="text-2xl font-black text-slate-900 dark:text-white leading-none">{formatCurrency(analytics.totalSpent)}</p>
                 </div>
              </div>
           </div>

           {/* Legenda Customizada Mobile Friendly */}
           <div className="mt-8 grid grid-cols-2 gap-3">
              {analytics.categoryData.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-800">
                   <div className="size-2 rounded-full shrink-0" style={{ backgroundColor: item.color }}></div>
                   <div className="flex-1 min-w-0">
                      <p className="text-[8px] font-black uppercase text-slate-400 truncate">{item.name}</p>
                      <p className="text-xs font-black text-slate-700 dark:text-slate-300">
                        {((item.value / analytics.totalSpent) * 100).toFixed(0)}%
                      </p>
                   </div>
                </div>
              ))}
           </div>
        </div>

        {/* CONSUMO POR ETAPA - UI ULTRA REFINADA */}
        <div className="bg-white dark:bg-slate-900 p-8 sm:p-10 rounded-[3rem] shadow-sm border border-slate-100 dark:border-slate-800 relative overflow-hidden">
           <div className="mb-8">
             <h3 className="text-xl font-black uppercase tracking-tight flex items-center gap-3">
               <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-xl text-amber-600"><Activity size={20} /></div>
               Eficiência por Etapa
             </h3>
             <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2 ml-12">Consumo do orçamento detalhado</p>
           </div>

           {isLocked && (
              <div className="absolute inset-0 z-20 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md flex flex-col items-center justify-center text-center p-10 space-y-6 animate-fadeIn">
                  <div className="size-24 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 rounded-[2rem] flex items-center justify-center shadow-xl ring-8 ring-white dark:ring-slate-900">
                     <Lock size={40} strokeWidth={2.5} />
                  </div>
                  <div className="max-w-xs space-y-3">
                     <h4 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight leading-none">Visão Bloqueada</h4>
                     <p className="text-sm font-bold text-slate-500 dark:text-slate-400 leading-relaxed">
                       O orçamento atual é simplificado. Ative o modo **PRO+** ou configure valores por etapa para visualizar.
                     </p>
                  </div>
                  <button 
                    onClick={onEditStages}
                    className="w-full py-5 bg-emerald-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl shadow-emerald-600/30 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-3"
                  >
                    Configurar Etapas Detalhadas <ChevronRight size={18}/>
                  </button>
              </div>
           )}

           <div className={`space-y-6 max-h-[480px] overflow-y-auto custom-scrollbar pr-4 ${isLocked ? 'blur-md opacity-20 select-none' : ''}`}>
              {analytics.stagesData.map(stage => {
                 let accentColor = 'emerald';
                 if (stage.status === 'warning') accentColor = 'amber';
                 else if (stage.status === 'danger') accentColor = 'rose';

                 return (
                    <div key={stage.id} className="group p-5 bg-slate-50 dark:bg-slate-950 rounded-[2rem] border border-slate-100 dark:border-slate-800 transition-all hover:border-slate-200 dark:hover:border-slate-700 shadow-sm">
                       <div className="flex justify-between items-start mb-4">
                          <div className="min-w-0">
                             <h4 className="text-xs font-black uppercase text-slate-800 dark:text-slate-200 tracking-wide truncate">{stage.name}</h4>
                             <div className="flex items-center gap-2 mt-1">
                                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{formatCurrency(stage.spent)} de {formatCurrency(stage.budget)}</span>
                             </div>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-sm border border-transparent
                            ${stage.status === 'healthy' ? 'bg-emerald-100 text-emerald-700' : ''}
                            ${stage.status === 'warning' ? 'bg-amber-100 text-amber-700 border-amber-200' : ''}
                            ${stage.status === 'danger' ? 'bg-rose-100 text-rose-700 border-rose-200 animate-pulse' : ''}
                          `}>
                             {stage.consumptionPercent.toFixed(0)}%
                          </div>
                       </div>
                       
                       <div className="relative pt-1">
                          <div className="flex mb-2 items-center justify-between">
                             <div className="h-3 w-full bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden shadow-inner">
                                <div 
                                  className={`h-full transition-all duration-1000 ease-out rounded-full bg-${accentColor}-500 shadow-[0_0_8px_rgba(0,0,0,0.1)]`}
                                  style={{ width: `${Math.min(stage.consumptionPercent, 100)}%` }}
                                ></div>
                             </div>
                          </div>
                       </div>
                       
                       {stage.status === 'danger' && (
                         <div className="mt-3 flex items-center gap-2 text-[9px] font-black text-rose-500 uppercase tracking-widest bg-rose-50 dark:bg-rose-900/10 p-2 rounded-xl border border-rose-100 dark:border-rose-900/30">
                            <AlertTriangle size={12} strokeWidth={3} />
                            Excedido em {formatCurrency(stage.spent - stage.budget)}
                         </div>
                       )}
                    </div>
                 );
              })}
           </div>
        </div>

      </div>
    </div>
  );
};
